import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './SocialUserAvatar.css';
import LiberiumLink from "./LiberiumLink";

const SocialUserAvatar = ({ user_id, className = '', size = 50, linkEnabled = true }) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [userName, setUserName] = useState("");
  const [isOnline, setIsOnline] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Charger les données de l'utilisateur
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user_id || user_id <= 0) {
        setIsLoading(false);
        setError(true);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(false);
        
        const userResponse = await sdk.models.PeupleUser.getUser({user_id});
        
        if (userResponse) {
          // Format du nom complet
          const fullName = [
            userResponse.first_name || '',
            userResponse.last_name || ''
          ].filter(Boolean).join(' ') || userResponse.name || "Utilisateur";
          
          setUserName(fullName);
          setIsOnline(userResponse.isOnline || false);
          
          // Définir l'URL de l'avatar avec la bonne base
          if (userResponse.avatar) {
            setAvatarUrl(`https://peupleqc.com/s/peupleqc-upload/${userResponse.avatar}`);
          } else {
            setAvatarUrl("https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg");
          }
        } else {
          setError(true);
          setAvatarUrl("https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg");
        }
      } catch (err) {
        console.error("Erreur lors du chargement de l'avatar:", err);
        setError(true);
        setAvatarUrl("https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user_id]);

  // Gestionnaire d'erreur pour l'image
  const handleImageError = () => {
    if (avatarUrl !== "https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg") {
      setAvatarUrl("https://peupleqc.com/s/peupleqc-upload/no-gender-avatar.jpg");
    }
  };

  // Styles dynamiques pour la taille
  const containerStyle = {
    width: `${size}px`,
    height: `${size}px`,
  };

  // Calcul de la taille du statut en ligne basé sur la taille de l'avatar
  const statusSize = Math.max(8, Math.min(16, size * 0.25));

  // Contenu de l'avatar
  const avatarContent = (
    <div 
      style={containerStyle}
      className={`avatau-wrapper ${className} ${isLoading ? 'avatau-loading' : ''} ${error ? 'avatau-error' : ''}`}
    >
      <div className="avatau-container">
        <div className="avatau-image-wrapper">
          {isLoading ? (
            <div className="avatau-loading-placeholder">
              <div className="avatau-loading-spinner"></div>
            </div>
          ) : (
            avatarUrl && (
              <img 
                src={avatarUrl} 
                alt={userName || "Utilisateur"}
                className="avatau-image"
                onError={handleImageError}
                loading="lazy"
              />
            )
          )}
        </div>
      </div>
      
      {/* Indicateur de statut en ligne - positionné en dehors du container circulaire */}
      {!isLoading && !error && (
        <div 
          className="avatau-online-status"
          style={{
            position: 'absolute',
            bottom: `-${statusSize * 0.2}px`,
            right: `-${statusSize * 0.2}px`,
            width: `${statusSize}px`,
            height: `${statusSize}px`,
            borderRadius: '50%',
            backgroundColor: isOnline ? '#42b883' : '#e74c3c',
            border: '2px solid #ffffff',
            zIndex: 3,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
          }}
          title={isOnline ? 'En ligne' : 'Hors ligne'}
        />
      )}
    </div>
  );

  // Si les liens sont désactivés, retourner directement le contenu
  if (!linkEnabled || !user_id || user_id <= 0) {
    return avatarContent;
  }

  // Retourner le contenu avec un lien
  return (
    <LiberiumLink 
      href={`/user/${user_id}`} 
      className="avatau-link"
    >
      {avatarContent}
    </LiberiumLink>
  );
};

export { SocialUserAvatar };